<template>
  <div>
    <div class="relative">
      <button
        v-if="isPaymentFailed && currentTeam && !isDemo"
        type="button"
        class="flex shrink-0 items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-[13px] font-medium text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/40"
        @click="goToBillingPortal">
        Update payment to publish
      </button>
      <template v-else>
        <div
          ref="buttonWrapperRef"
          v-tooltip.bottom="buttonSubtext"
          class="inline-flex shrink-0"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave">
          <button
            type="button"
            :disabled="publishDisabled"
            class="bg-primary-600 hover:bg-primary-500 focus-visible:outline-primary-600 disabled:hover:bg-primary-600 flex items-center rounded-md px-3 py-1.5 text-[13px] font-semibold tabular-nums text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            @click="attemptToPublish">
            {{ buttonLabel }}
          </button>
        </div>

        <!-- Hover preview -->
        <PublishChangesPreview
          v-if="configChanges.length > 0"
          :show="showPreview"
          :changes="configChanges"
          :button-ref="buttonWrapperRef"
          position="bottom" />
      </template>
    </div>

    <PublishErrors
      :visible="publishErrorsVisible"
      :validation-errors="validationErrors"
      @update:visible="publishErrorsVisible = $event" />
    <PublishChangesModal
      :visible="publishChangesModalVisible"
      :changes="configChanges"
      :is-publishing="isPublishingChanges"
      @update:visible="publishChangesModalVisible = $event"
      @publish="handlePublishWithMessage" />
    <SnapshotCreationModal :open="openCreateSnapshotModal" @update-visible="openCreateSnapshotModal = $event" />
    <PublishSubscribeModal
      :visible="publishSubscribeModalVisible"
      :plans="subscriptionPlans"
      :publish-message="pendingPublishMessage"
      @update:visible="handlePublishSubscribeVisibleChange"
      @subscribed="handleSubscribedAndPublished" />
  </div>
</template>

<script setup lang="ts">
import axios from 'axios'
import api from '@/services/axiosApi'
import { PublishState } from '@/stores/configStore'
import { recordAnalyticsEvent, AnalyticsEventTypes } from '@/services/analytics'
import {
  clearActivationWizardPublishCorrelation,
  consumeActivationWizardPublishCorrelationForPublishClick
} from '@/modules/onboarding/services/activationWizardTelemetry'
import type { RegistrationPlanApiResponse } from '@/modules/user/services/registrationPlans'
import { useConfigChanges } from '@/composables/useConfigChanges'
import type { ConfigChange } from '@/composables/useConfigChanges'

import SnapshotCreationModal from '@/components/common/SnapshotCreationModal.vue'
import PublishChangesModal from '@/components/common/PublishChangesModal.vue'
import PublishChangesPreview from '@/components/common/PublishChangesPreview.vue'
import PublishSubscribeModal from '@/components/common/PublishSubscribeModal.vue'
import { useIsFetching } from '@tanstack/vue-query'
import { watchDebounced } from '@vueuse/core'

const authStore = useAuthStore()
const { user, isAdmin } = storeToRefs(authStore)

const configStore = useConfigStore()
const notificationStore = useNotificationStore()
const { publish } = configStore
const { currentTeam, currentTeamPid, config, fullResponse } = storeToRefs(configStore)
const { isUpdatingConfig, publishState, isDirty } = storeToRefs(configStore)

const subscriptionStore = useSubscriptionStore()
const { isActive, isPaymentFailed, hasNoSubscription } = storeToRefs(subscriptionStore)

const isDemo = inject('isDemo', false)

const { goToBillingPortal } = useBillingUrl()

const { analyzeChanges } = useConfigChanges()

const isFetching = useIsFetching()

const validationErrors = ref([])
const openCreateSnapshotModal = ref(false)

const publishErrorsVisible = ref(false)
const publishChangesModalVisible = ref(false)
const configChanges = ref<ConfigChange[]>([])
const isPublishingChanges = ref(false)
const publishSubscribeModalVisible = ref(false)
const subscriptionPlans = ref<RegistrationPlanApiResponse[]>([])
const pendingPublishMessage = ref<string | null>(null)
const showPreview = ref(false)
const buttonWrapperRef = ref<HTMLElement | null>(null)

const resetPendingPublishState = () => {
  pendingPublishMessage.value = null
  subscriptionPlans.value = []
}

// Analyze changes between draft and published config
const analyzeConfigChanges = async () => {
  if (!config.value || !fullResponse.value) {
    configChanges.value = []
    return
  }

  const publishedConfig = fullResponse.value.stable || null
  configChanges.value = await analyzeChanges(config.value, publishedConfig)
}

const loadSubscriptionPlans = async () => {
  const { data } = await api.get(`/api/${currentTeamPid.value}/billing/availablePlans`)
  subscriptionPlans.value = data.plans || []
}

// Watch for config changes with debouncing to avoid excessive analysis during rapid edits
watchDebounced(
  [config, fullResponse],
  () => {
    analyzeConfigChanges()
  },
  { debounce: 1000, deep: true, immediate: true }
)

const availableToPublish = computed(() => {
  // return store.state.config.status === 'saved'
  return true
})

const syncHappening = computed(() => {
  return isFetching.value > 0 || isUpdatingConfig.value
})

const publishDisabled = computed(() => {
  return !isAdmin.value && (syncHappening.value || !currentTeam.value || configChanges.value.length === 0)
})

const buttonLabel = computed(() => {
  const count = configChanges.value.length
  if (count === 0) {
    return 'Fully synced'
  }

  return `Publish ${count} ${count === 1 ? 'change' : 'changes'}`
})

const buttonSubtext = computed(() => {
  const count = configChanges.value.length
  if (count === 0) {
    return 'All changes published to your website'
  }

  return 'Unpublished changes are not live'
})

const attemptToPublish = async () => {
  if (isDemo.value) {
    alert(`Drats! Publishing isn't available within the demo sandbox.`)
    return
  }
  const errors = configStore.validateForPublish()

  if (errors.length > 0) {
    validationErrors.value = errors
    publishErrorsVisible.value = true
  } else {
    // Analyze changes and show modal
    await analyzeConfigChanges()
    publishChangesModalVisible.value = true
  }
}

const handlePublishWithMessage = async (message: string) => {
  isPublishingChanges.value = true
  try {
    const activationWizardPublishCorrelation =
      consumeActivationWizardPublishCorrelationForPublishClick()
    if (activationWizardPublishCorrelation) {
      recordAnalyticsEvent(
        AnalyticsEventTypes.ACTIVATION_WIZARD_PUBLISH_CLICKED,
        activationWizardPublishCorrelation
      )
    }

    if (hasNoSubscription.value) {
      pendingPublishMessage.value = message

      try {
        await loadSubscriptionPlans()
        publishChangesModalVisible.value = false
        publishSubscribeModalVisible.value = true
        recordAnalyticsEvent(AnalyticsEventTypes.PAYWALL_HIT)
        return
      } catch {
        publishChangesModalVisible.value = true
        pendingPublishMessage.value = null
        notificationStore.addError(
          'Could not load billing plans. Please try again before publishing.'
        )
        return
      }
    }

    await publish(message)
    publishChangesModalVisible.value = false
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 402 &&
      error.response?.data &&
      typeof error.response.data === 'object' &&
      'requires_subscription' in error.response.data &&
      error.response.data.requires_subscription
    ) {
      pendingPublishMessage.value = message
      const plans = error.response.data.plans
      subscriptionPlans.value = Array.isArray(plans) ? plans : []
      publishChangesModalVisible.value = false
      publishSubscribeModalVisible.value = true
      recordAnalyticsEvent(AnalyticsEventTypes.PAYWALL_HIT)
    } else {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'code' in error.response.data &&
        error.response.data.code === 'billing_state_conflict'
      ) {
        publishChangesModalVisible.value = false
      }
      clearActivationWizardPublishCorrelation()
    }
    // Other errors are handled by the configStore publish mutation's onError
  } finally {
    isPublishingChanges.value = false
  }
}

const handlePublishSubscribeVisibleChange = (visible: boolean) => {
  publishSubscribeModalVisible.value = visible
  if (!visible) {
    resetPendingPublishState()
  }
}

const handleSubscribedAndPublished = () => {
  resetPendingPublishState()
  publishSubscribeModalVisible.value = false
  publishChangesModalVisible.value = false
}

const handleMouseEnter = () => {
  if (configChanges.value.length > 0) {
    showPreview.value = true
  }
}

const handleMouseLeave = () => {
  showPreview.value = false
}

const buttonText = computed(() => {
  switch (publishState.value) {
    case PublishState.Loading:
      return 'Loading'
    case PublishState.Published:
      return 'Published'
    case PublishState.Dirty:
    case PublishState.Saved:
      return 'Publish To Site'
    default:
      return 'Unknown'
  }
})
</script>

<!-- <script>
import PrePublishIntegrate from '@/components/prepublish/PrePublishIntegrate.vue'
import Button from 'primevue/button'

export default {
  components: {
    PrePublishIntegrate,
    Button
  },

  data() {
    return {
      modalScreen: null
    }
  },

  computed: {
    availableToPublish() {
      if (process.env.NODE_ENV === 'development') return true
      return this.$store.state.config.status === 'saved'
    },

    buttonText() {
      if (process.env.NODE_ENV === 'development') return 'Publish (local)'
      switch (this.$store.state.config.status) {
        case 'loading':
          return 'Loading'

        case 'published':
          return 'Published'

        case 'dirty':
        case 'saving':
          return 'Saving...'

        case 'publishing':
          return 'Publishing...'

        // The only one that enables the button to work
        case 'saved':
        default:
          return 'Publish to site'
      }
    }
  },

  methods: {
    publishIfPossible() {
      if (
        this.$store.getters['config/draftHasLogicWhere'](
          (logic) => logic.$source === 'espplaceholder'
        )
      ) {
        if (this.$store.state.integrations.isLoading) {
          return // TODO don't be lazy and buggy
        }

        this.modalScreen = 'integrate'
        return
      }

      //                // Nope. We don't worry about snippet til after doing the actual publish.
      //                if (!this.$store.getters['gettingStarted/done_snippet']) {
      //                    this.modalScreen = 'snippet';
      //                    return;
      //                }

      this.modalScreen = null
      this.definitelyPublish()
    },

    definitelyPublish() {
      //                alert('Would publish now.');return;
      this.$emit('before')
      this.$store.dispatch('config/PUBLISH').then(
        (response) => this.$emit('success', response),
        (error) => this.$emit('failure', error)
      )
    }
  }
}
</script>

<style type="less"></style> -->
